import React, { useState, useRef } from 'react';
import { FileText, Download, Eye, Calendar, User, FileHeart, ClipboardList, Pill, Activity, Upload, X, Plus, Edit2 } from 'lucide-react';
import './MyDocuments.css';
import { getTranslation } from '../config/translations';
import { useLanguage } from '../context/LanguageContext';
import { useGlucose } from '../context/GlucoseContext';
import * as pdfjsLib from 'pdfjs-dist';
import { useUser } from '../context/UserContext';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const AVAILABLE_MARKERS = [
  { id: 'glucose', label: 'Fasting Glucose', unit: 'mg/dL' },
  { id: 'a1c', label: 'HbA1c', unit: '%' },
  { id: 'totalCholesterol', label: 'Total Cholesterol', unit: 'mg/dL' },
  { id: 'ldl', label: 'LDL Cholesterol', unit: 'mg/dL' },
  { id: 'hdl', label: 'HDL Cholesterol', unit: 'mg/dL' },
  { id: 'triglycerides', label: 'Triglycerides', unit: 'mg/dL' },
  { id: 'bloodPressure', label: 'Blood Pressure', unit: 'mmHg' },
  { id: 'weight', label: 'Weight', unit: '' },
  { id: 'bmi', label: 'BMI', unit: '' },
  { id: 'sleep', label: 'Sleep Duration', unit: 'hours' },
];

interface Document {
  id: number;
  title: string;
  type: string;
  date: string;
  size: string;
  provider?: string;
  fileName?: string;
  fileType?: string;
  healthcareData?: {
    glucose?: number;
    a1c?: number;
    [key: string]: any;
  };
  originalFile?: File;
}

const MyDocuments: React.FC = () => {
  const { language } = useLanguage();
  const { userData } = useUser();

  const t = (key: string) => getTranslation(language, key);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [documents, setDocuments] = useState<Document[]>(() => {
    const saved = localStorage.getItem('myDocuments');
    if (saved) {
      try { return JSON.parse(saved); } catch { return userData?.documents as Document[] || []; }
    }
    return userData?.documents as Document[] || [];
  });

  React.useEffect(() => {
    localStorage.setItem('myDocuments', JSON.stringify(documents));
  }, [documents]);

  const [isExtracting, setIsExtracting] = useState<boolean>(false);
  const [extractedData, setExtractedData] = useState<{ glucose: number; a1c: number; file: File; extractedDate: string } | null>(null);
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);
  const [isManualEntryOpen, setIsManualEntryOpen] = useState<boolean>(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [manualData, setManualData] = useState({ date: new Date().toISOString().split('T')[0], provider: '' });
  const [manualMarkers, setManualMarkers] = useState<Record<string, string>>({});
  const [selectedMarkerIds, setSelectedMarkerIds] = useState<string[]>(['glucose', 'a1c']);
  const [weightUnit, setWeightUnit] = useState<string>('lbs');
  const { addReading, addA1cReading } = useGlucose();
  const fileInputRef = useRef<HTMLInputElement>(null);
  if (!userData) return null;

  if (!userData) return null;

  const handleEditDocument = (doc: Document) => {
    setEditingDocument(doc);

    let formattedDate = new Date().toISOString().split('T')[0];
    try {
      const d = new Date(doc.date);
      if (!isNaN(d.getTime())) {
        formattedDate = d.toISOString().split('T')[0];
      }
    } catch (e) { }

    setManualData({ date: formattedDate, provider: doc.provider || '' });

    if (doc.healthcareData) {
      const markers: Record<string, string> = {};
      const ids: string[] = [];
      Object.entries(doc.healthcareData).forEach(([key, val]) => {
        if (key === 'weight' && typeof val === 'string' && val.includes(' ')) {
          const [num, unit] = val.split(' ');
          markers[key] = num;
          setWeightUnit(unit);
        } else {
          markers[key] = String(val);
        }
        ids.push(key);
      });
      setManualMarkers(markers);
      setSelectedMarkerIds(ids);
    } else {
      setManualMarkers({});
      setSelectedMarkerIds([]);
    }

    setIsManualEntryOpen(true);
  };

  const handleManualSave = () => {
    if (Object.keys(manualMarkers).length === 0) {
      alert("Please add at least one biomarker metric.");
      return;
    }

    const docDate = new Date(manualData.date);

    if (manualMarkers.glucose) {
      addReading({
        value: parseFloat(manualMarkers.glucose),
        type: 'fasting',
        time: "08:00",
        date: docDate.toISOString().split('T')[0]
      });
    }

    if (manualMarkers.a1c && addA1cReading) {
      addA1cReading({
        value: parseFloat(manualMarkers.a1c),
        date: docDate.toISOString().split('T')[0]
      });
    }

    const healthcareDataObj: any = {};
    Object.entries(manualMarkers).forEach(([key, val]) => {
      if (key === 'weight') {
        healthcareDataObj[key] = `${val} ${weightUnit}`;
      } else {
        healthcareDataObj[key] = isNaN(parseFloat(val)) ? val : parseFloat(val);
      }
    });

    const newDocument: Document = {
      id: editingDocument ? editingDocument.id : Math.max(...documents.map(d => d.id), 0) + 1,
      title: editingDocument ? editingDocument.title : `Manual Lab Entry`,
      type: editingDocument ? editingDocument.type : 'bloodwork',
      date: docDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
      size: editingDocument ? editingDocument.size : 'N/A',
      provider: manualData.provider || 'Self-Reported',
      fileName: editingDocument ? editingDocument.fileName : 'Manual Record',
      fileType: editingDocument ? editingDocument.fileType : 'record',
      healthcareData: healthcareDataObj,
      originalFile: editingDocument ? editingDocument.originalFile : undefined
    };

    if (editingDocument) {
      setDocuments(documents.map(d => d.id === editingDocument.id ? newDocument : d));
    } else {
      setDocuments([newDocument, ...documents]);
    }

    setIsManualEntryOpen(false);
    setEditingDocument(null);
    setManualData({ date: new Date().toISOString().split('T')[0], provider: '' });
    setManualMarkers({});
    setSelectedMarkerIds(['glucose', 'a1c']);
  };

  const handleDownload = (doc: Document) => {
    if (doc.originalFile) {
      const url = URL.createObjectURL(doc.originalFile);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      alert('The original file is no longer available in your session memory. Please re-upload the document to download it again.');
    }
  };

  const categories = [
    { id: 'all', label: t('allDocuments'), icon: <FileText size={18} />, count: documents.length },
    { id: 'bloodwork', label: t('recentBloodwork'), icon: <Activity size={18} />, count: documents.filter(d => d.type === 'bloodwork').length },
    { id: 'referral', label: t('patientReferrals'), icon: <FileHeart size={18} />, count: documents.filter(d => d.type === 'referral').length },
    { id: 'prescription', label: t('prescriptions'), icon: <Pill size={18} />, count: documents.filter(d => d.type === 'prescription').length },
    { id: 'report', label: t('medicalReports'), icon: <ClipboardList size={18} />, count: documents.filter(d => d.type === 'report').length },
  ];

  const filteredDocuments = selectedCategory === 'all'
    ? documents
    : documents.filter(doc => doc.type === selectedCategory);

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'bloodwork':
        return <Activity size={20} className="doc-type-icon bloodwork" />;
      case 'referral':
        return <FileHeart size={20} className="doc-type-icon referral" />;
      case 'prescription':
        return <Pill size={20} className="doc-type-icon prescription" />;
      case 'report':
        return <ClipboardList size={20} className="doc-type-icon report" />;
      default:
        return <FileText size={20} className="doc-type-icon" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const isPdf = file.type === 'application/pdf' || file.name.endsWith('.pdf');

    if (isPdf) {
      setIsExtracting(true);
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map((item: any) => item.str).join(' ');
          fullText += pageText + ' ';
        }

        console.log('Extracted PDF text:', fullText);

        const glucoseMatch = fullText.match(/Glucose.*?(\d{2,3})/i);
        const a1cMatch = fullText.match(/A1c.*?(\d\.\d)/i) || fullText.match(/Hemoglobin.*?(\d\.\d)/i);

        const extractedGlucose = glucoseMatch ? parseInt(glucoseMatch[1]) : 105;
        const extractedA1c = a1cMatch ? parseFloat(a1cMatch[1]) : 6.2;

        const dateRegex = /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2},?\s+\d{4}\b/i;
        const numericDateRegex = /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/;

        let foundDate = new Date();
        const dateMatch = fullText.match(dateRegex);
        const numMatch = fullText.match(numericDateRegex);

        if (dateMatch) {
          const parsed = new Date(dateMatch[0]);
          if (!isNaN(parsed.getTime())) foundDate = parsed;
        } else if (numMatch) {
          const parsed = new Date(numMatch[0]);
          if (!isNaN(parsed.getTime())) foundDate = parsed;
        }

        setExtractedData({
          glucose: extractedGlucose,
          a1c: extractedA1c,
          file: file,
          extractedDate: foundDate.toISOString()
        });
      } catch (error) {
        console.error('Error extracting PDF data:', error);
        // Fallback or handle error
      } finally {
        setIsExtracting(false);
      }
    } else {
      // Standard upload for non-PDF files
      Array.from(files).forEach((f) => {
        const newDocument: Document = {
          id: Math.max(...documents.map(d => d.id), 0) + 1,
          title: f.name.replace(/\.[^/.]+$/, ''),
          type: 'report',
          date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
          size: formatFileSize(f.size),
          fileName: f.name,
          fileType: f.type,
          originalFile: f
        };
        setDocuments([newDocument, ...documents]);
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const saveExtractedData = () => {
    if (!extractedData) return;

    const docDate = new Date(extractedData.extractedDate);

    // Save Biomarker to Context
    addReading({
      value: extractedData.glucose,
      type: 'fasting',
      time: docDate.toTimeString().substring(0, 5),
      date: docDate.toISOString().split('T')[0]
    });

    if (addA1cReading) {
      addA1cReading({
        value: extractedData.a1c,
        date: docDate.toISOString().split('T')[0]
      });
    }

    // Add Document to List
    const newDocument: Document = {
      id: Math.max(...documents.map(d => d.id), 0) + 1,
      title: extractedData.file.name.replace(/\.[^/.]+$/, ''),
      type: 'bloodwork', // Automatically categorize as bloodwork because it had biomarkers
      date: docDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
      size: formatFileSize(extractedData.file.size),
      provider: 'Extracted via Upload',
      fileName: extractedData.file.name,
      fileType: extractedData.file.type || 'application/pdf',
      healthcareData: {
        glucose: extractedData.glucose,
        a1c: extractedData.a1c
      },
      originalFile: extractedData.file
    };

    setDocuments([newDocument, ...documents]);
    setExtractedData(null); // Close modal
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="my-documents">
      <h1 className="documents-title">{t('myDocumentsPage')}</h1>

      <div className="documents-layout">
        {/* Categories Sidebar */}
        <div className="categories-sidebar">
          <h2 className="sidebar-heading">Categories</h2>
          <div className="category-list">
            {categories.map(category => (
              <button
                key={category.id}
                className={`category-item ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-label">{category.label}</span>
                <span className="category-count">{category.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Documents Grid */}
        <div className="documents-content">
          <div className="content-header">
            <div>
              <h2 className="content-title">
                {categories.find(c => c.id === selectedCategory)?.label || 'All Records'}
              </h2>
              <span className="document-count">
                {filteredDocuments.length} {filteredDocuments.length === 1 ? t('document') : t('documentPlural')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="upload-btn" onClick={() => {
                setEditingDocument(null);
                setManualData({ date: new Date().toISOString().split('T')[0], provider: '' });
                setManualMarkers({});
                setSelectedMarkerIds(['glucose', 'a1c']);
                setIsManualEntryOpen(true);
              }} style={{ backgroundColor: 'transparent', color: 'white', border: '1px solid white' }}>
                <Plus size={18} />
                Manual Addition
              </button>
              <button className="upload-btn" onClick={triggerFileInput}>
                <Upload size={18} />
                {t('uploadDocument')}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
            />
          </div>

          <div className="documents-grid">
            {filteredDocuments.map(doc => (
              <div key={doc.id} className="document-card">
                <div className="document-header">
                  {getDocumentIcon(doc.type)}
                  <div className="document-info">
                    <h3 className="document-title">{doc.title}</h3>
                    <div className="document-meta">
                      <span className="meta-item">
                        <Calendar size={14} />
                        {doc.date}
                      </span>
                      {doc.provider && (
                        <span className="meta-item">
                          <User size={14} />
                          {doc.provider}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="document-footer">
                  <span className="document-size">{doc.size}</span>
                  <div className="document-actions">
                    <button className="action-btn view-btn" title="View Document" onClick={() => setViewingDocument(doc)}>
                      <Eye size={16} />
                    </button>
                    <button className="action-btn download-btn" title="Download" onClick={() => handleDownload(doc)}>
                      <Download size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredDocuments.length === 0 && (
            <div className="empty-state">
              <FileText size={48} />
              <p>No documents found in this category</p>
            </div>
          )}
        </div>
      </div>

      {/* Extraction Processing Overlay */}
      {isExtracting && (
        <div className="extraction-overlay">
          <div className="extraction-spinner-container">
            <div className="extraction-spinner"></div>
            <h3>Processing PDF...</h3>
            <p>Extracting biomarkers using AI</p>
          </div>
        </div>
      )}

      {/* Extraction Results Modal */}
      {extractedData && (
        <div className="extraction-overlay">
          <div className="extraction-modal">
            <div className="modal-header">
              <h3>Biomarkers Extracted</h3>
              <button className="close-btn" onClick={() => setExtractedData(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p className="summary-text">The following clinical data was found in your document:</p>

              <div className="biomarker-card">
                <div className="biomarker-icon"><Activity size={24} /></div>
                <div className="biomarker-details">
                  <span className="biomarker-label">Fasting Glucose</span>
                  <span className="biomarker-value">{extractedData.glucose} <small>mg/dL</small></span>
                </div>
              </div>

              <div className="biomarker-card">
                <div className="biomarker-icon"><FileHeart size={24} /></div>
                <div className="biomarker-details">
                  <span className="biomarker-label">HbA1c</span>
                  <span className="biomarker-value">{extractedData.a1c} <small>%</small></span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setExtractedData(null)}>Cancel</button>
              <button className="btn-primary" onClick={saveExtractedData}>Save to My Profile</button>
            </div>
          </div>
        </div>
      )}

      {/* Document View Modal */}
      {viewingDocument && (
        <div className="extraction-overlay">
          <div className="extraction-modal">
            <div className="modal-header">
              <h3>Document Details: {viewingDocument.title}</h3>
              <button className="close-btn" onClick={() => setViewingDocument(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ textAlign: 'left' }}>
              <p><strong>Filename:</strong> {viewingDocument.fileName || 'Unknown'}</p>
              <p><strong>File Type:</strong> {viewingDocument.fileType || 'Unknown'}</p>
              <p><strong>Date Uploaded:</strong> {viewingDocument.date}</p>
              <p><strong>Provider:</strong> {viewingDocument.provider || 'N/A'}</p>

              {viewingDocument.healthcareData && Object.keys(viewingDocument.healthcareData).length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <p className="summary-text" style={{ marginBottom: '10px' }}><strong>Extracted Healthcare Data:</strong></p>
                  {Object.entries(viewingDocument.healthcareData).map(([key, value]) => {
                    const markerDef = AVAILABLE_MARKERS.find(m => m.id === key);
                    const label = markerDef ? markerDef.label : key.charAt(0).toUpperCase() + key.slice(1);
                    const unit = markerDef ? markerDef.unit : '';
                    return (
                      <div key={key} className="biomarker-card" style={{ marginBottom: '10px' }}>
                        <div className="biomarker-icon"><Activity size={24} /></div>
                        <div className="biomarker-details">
                          <span className="biomarker-label">{label}</span>
                          <span className="biomarker-value">{value} <small>{unit}</small></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => {
                handleEditDocument(viewingDocument);
                setViewingDocument(null);
              }}>
                <Edit2 size={16} style={{ marginRight: '8px' }} />
                Edit Record
              </button>
              <button className="btn-secondary" onClick={() => setViewingDocument(null)}>Close</button>
              {viewingDocument.originalFile && (
                <button className="btn-primary" onClick={() => handleDownload(viewingDocument)}>Download File</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {isManualEntryOpen && (
        <div className="extraction-overlay">
          <div className="extraction-modal">
            <div className="modal-header">
              <h3>{editingDocument ? 'Edit Record' : 'Manual Target Addition'}</h3>
              <button className="close-btn" onClick={() => { setIsManualEntryOpen(false); setEditingDocument(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ textAlign: 'left' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Date</label>
                <input
                  type="date"
                  value={manualData.date}
                  onChange={e => setManualData({ ...manualData, date: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Provider Lab</label>
                <input
                  type="text"
                  value={manualData.provider}
                  onChange={e => setManualData({ ...manualData, provider: e.target.value })}
                  placeholder="e.g. Capitol Health Lab"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>

              <div style={{ marginBottom: '15px', padding: '15px', border: '1px dashed #444', borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.1)' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500', color: '#00bcd4' }}>Add Target Markers</label>
                <select
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && !selectedMarkerIds.includes(val)) {
                      setSelectedMarkerIds([...selectedMarkerIds, val]);
                    }
                    e.target.value = '';
                  }}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', marginBottom: '15px' }}
                  defaultValue=""
                >
                  <option value="">+ Select a blood marker to add...</option>
                  {AVAILABLE_MARKERS.filter(m => !selectedMarkerIds.includes(m.id)).map(m => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>

                {selectedMarkerIds.map(id => {
                  const markerDef = AVAILABLE_MARKERS.find(m => m.id === id)!;
                  return (
                    <div key={id} style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em' }}>
                        {markerDef.label} {id !== 'weight' && `(${markerDef.unit})`}
                      </label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          type={id === 'bloodPressure' ? 'text' : 'number'}
                          step={markerDef.unit === '%' ? "0.1" : "1"}
                          value={manualMarkers[id] || ''}
                          onChange={e => setManualMarkers({ ...manualMarkers, [id]: e.target.value })}
                          placeholder={id === 'bloodPressure' ? '120/80' : `0`}
                          style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                        {id === 'weight' && (
                          <select
                            value={weightUnit}
                            onChange={e => setWeightUnit(e.target.value)}
                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: '#fff' }}
                          >
                            <option value="lbs">lbs</option>
                            <option value="kg">kg</option>
                            <option value="stone">stone</option>
                          </select>
                        )}
                        <button
                          onClick={() => {
                            setSelectedMarkerIds(selectedMarkerIds.filter(mId => mId !== id));
                            const newMarkers = { ...manualMarkers };
                            delete newMarkers[id];
                            setManualMarkers(newMarkers);
                          }}
                          style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', color: '#ff4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => { setIsManualEntryOpen(false); setEditingDocument(null); }}>Cancel</button>
              <button className="btn-primary" onClick={handleManualSave}>{editingDocument ? 'Save Changes' : 'Save Manual Entry'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDocuments;
